// WithdrawalProjectionCard — symptom-aware projection (PR 3A, Gap 4).
//
// 2x3 grid of micro-sparklines (6 symptoms × p10/p50/p90 bands). Tap a
// cell → bottom sheet with the full 21-day curve + per-symptom
// "what does high mean" copy. Never a single point estimate.
//
// Mobile discipline:
//  - 2x3 CSS grid fits 375px-wide card with each cell ~140×80.
//  - SVG sparklines render at exact cell size (ResizeObserver-free)
//    by setting viewBox + preserveAspectRatio.
//  - prefers-reduced-motion: no entrance animation; just appears.
//  - Bottom sheet for detail; uses existing BottomSheet primitive.

import { useMemo, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Skeleton } from '../../components/ui/Skeleton';
import { BottomSheet } from '../../components/ui/BottomSheet';
import { useApi } from '../../hooks/useApi';
import type { SymptomKey } from '../../../shared/withdrawalPriors';

interface ProjectionPoint {
  day: number;
  daysAhead: number;
  p10: number;
  p50: number;
  p90: number;
  cohortSize: number;
}
interface ProjectionSymptom {
  symptom: SymptomKey;
  label: string;
  highMeaning: string;
  curve: ProjectionPoint[];
}
interface ProjectionResponse {
  baselineBand: 'low' | 'med' | 'high' | null;
  sowsBand: 'mild' | 'mod' | 'severe' | null;
  currentDayIndex: number | null;
  symptoms: ProjectionSymptom[];
  cohortHits: number;
  cellCount: number;
}

interface WithdrawalProjectionCardProps {
  dayIndex: number | null;
}

export function WithdrawalProjectionCard({ dayIndex }: WithdrawalProjectionCardProps) {
  const res = useApi<ProjectionResponse>(
    dayIndex != null ? '/api/insights/withdrawal-projection' : null,
  );
  const [openSymptom, setOpenSymptom] = useState<ProjectionSymptom | null>(null);

  if (dayIndex == null) return null;

  // Loading — render the card frame with skeleton cells so the Today
  // view doesn't reflow when the projection lands.
  if (res.status === 'loading' || res.status === 'idle') {
    return (
      <Card className="projection-card" accentLeft>
        <div className="projection-card-header">
          <span className="projection-card-eyebrow">What's likely</span>
          <span className="projection-card-headline">
            Next 21 days · ranges, not predictions
          </span>
        </div>
        <div className="projection-grid" aria-hidden="true">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} height={88} />
          ))}
        </div>
      </Card>
    );
  }

  // Error — surface a retry instead of silently dropping the card, so a
  // transient 500 doesn't make a Day-6 user think they have no
  // projection at all.
  if (res.status === 'error') {
    return (
      <Card className="projection-card" accentLeft>
        <div className="projection-card-header">
          <span className="projection-card-eyebrow">What's likely</span>
          <span className="projection-card-headline">Next 21 days</span>
        </div>
        <p className="projection-card-helper dim text-caption">
          Couldn't load your projection right now.
        </p>
        <Button variant="secondary" onClick={res.refresh}>
          Try again
        </Button>
      </Card>
    );
  }

  return (
    <>
      <Card className="projection-card" accentLeft>
        <div className="projection-card-header">
          <span className="projection-card-eyebrow">What's likely</span>
          <span className="projection-card-headline">
            Next 21 days · ranges, not predictions
          </span>
        </div>
        <p className="projection-card-helper dim text-caption">
          {res.data.cohortHits > 0
            ? 'Each band is the 10th–90th percentile from your dose-history cohort + literature. Tap one for the full curve.'
            : 'Each band is the 10th–90th percentile from literature priors — they personalize as you log. Tap one for the full curve.'}
        </p>
        <div className="projection-grid">
          {res.data.symptoms.map((sym) => (
            <button
              key={sym.symptom}
              type="button"
              className="projection-cell"
              onClick={() => setOpenSymptom(sym)}
              aria-label={`Open ${sym.label} projection`}
            >
              <span className="projection-cell-label">{sym.label}</span>
              <Sparkline curve={sym.curve} />
              <span className="projection-cell-range">
                {summarizeBand(sym.curve)}
              </span>
            </button>
          ))}
        </div>
      </Card>

      <BottomSheet
        open={openSymptom !== null}
        onOpenChange={(o) => {
          if (!o) setOpenSymptom(null);
        }}
        title={openSymptom ? `${openSymptom.label} — 21-day range` : ''}
        description={openSymptom?.highMeaning}
      >
        {openSymptom ? (
          <div
            className="stack"
            style={{ '--stack-gap': 'var(--space-3)' } as React.CSSProperties}
          >
            <Sparkline curve={openSymptom.curve} large />
            <ul className="projection-detail-list">
              {[0, 6, 13, 20].map((idx) => {
                const p = openSymptom.curve[idx];
                if (!p) return null;
                return (
                  <li key={idx}>
                    <span className="projection-detail-day">Day {p.day + 1}</span>
                    <span className="projection-detail-band">
                      <strong>{p.p50.toFixed(1)}</strong>
                      <span className="dim"> ({p.p10.toFixed(1)}–{p.p90.toFixed(1)})</span>
                    </span>
                  </li>
                );
              })}
            </ul>
            <p className="muted text-caption">
              {openSymptom.curve[0].cohortSize > 0
                ? `${openSymptom.curve[0].cohortSize} similar users informed today's band.`
                : 'Based on literature priors. Once enough users opt in, your cohort will refine this.'}
            </p>
          </div>
        ) : null}
      </BottomSheet>
    </>
  );
}

function summarizeBand(curve: ProjectionPoint[]): string {
  // Always a RANGE, never a point estimate — the "never a single point
  // estimate" rule covers the glanceable cell summary too, not just the
  // sparkline. Show the p10–p90 band at the window's worst point.
  if (curve.length === 0) return '';
  const worst = curve.reduce((max, p) => (p.p50 > max.p50 ? p : max), curve[0]);
  const lo = Math.round(worst.p10);
  const hi = Math.round(worst.p90);
  return lo === hi ? `peak ≤${hi}` : `peak ${lo}–${hi}`;
}

interface SparklineProps {
  curve: ProjectionPoint[];
  large?: boolean;
}

function Sparkline({ curve, large }: SparklineProps) {
  const w = large ? 320 : 132;
  const h = large ? 90 : 40;
  const padX = 4;
  const padY = 4;
  const innerW = w - padX * 2;
  const innerH = h - padY * 2;
  // Y range: 0–10 (severity scale).
  const yMax = 10;
  const points = useMemo(
    () =>
      curve.map((p, i) => {
        const x = padX + (innerW * i) / (curve.length - 1);
        const y10 = padY + innerH * (1 - p.p10 / yMax);
        const y50 = padY + innerH * (1 - p.p50 / yMax);
        const y90 = padY + innerH * (1 - p.p90 / yMax);
        return { x, y10, y50, y90 };
      }),
    [curve, innerW, innerH],
  );

  // p10-p90 band as a closed polygon
  const bandPath = useMemo(() => {
    if (points.length === 0) return '';
    const top = points.map((p) => `${p.x},${p.y90}`).join(' L ');
    const bottom = [...points].reverse().map((p) => `${p.x},${p.y10}`).join(' L ');
    return `M ${top} L ${bottom} Z`;
  }, [points]);

  // p50 line
  const midPath = useMemo(() => {
    if (points.length === 0) return '';
    return `M ${points.map((p) => `${p.x},${p.y50}`).join(' L ')}`;
  }, [points]);

  return (
    <svg
      className="projection-sparkline"
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <path d={bandPath} className="projection-sparkline-band" />
      <path d={midPath} className="projection-sparkline-line" />
    </svg>
  );
}
